import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Loader2, CalendarDays, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import AdminLayout, { AdminPageHeader, AdminTable } from "./layout";
import { format, parseISO } from "date-fns";

const COLUMNS = ["Event", "Type", "Date", "Location", "Featured", "Actions"];

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

interface Event {
  id: number;
  title: string;
  type: "conference" | "webinar" | "seminar" | "workshop";
  organiser: string;
  date: string;
  endDate?: string | null;
  location?: string | null;
  url: string;
  description?: string | null;
  isFeatured: boolean;
  createdAt: string;
}

type EForm = Omit<Event, "id" | "createdAt">;

const EMPTY: EForm = {
  title: "",
  type: "conference",
  organiser: "",
  date: new Date().toISOString().slice(0, 16),
  endDate: "",
  location: "",
  url: "",
  description: "",
  isFeatured: false,
};

const TYPE_LABELS: Record<string, string> = {
  conference: "Conference",
  webinar: "Webinar",
  seminar: "Seminar",
  workshop: "Workshop",
};

const TYPE_COLOURS: Record<string, string> = {
  conference: "bg-teal-100 text-teal-800 border-teal-200",
  webinar: "bg-indigo-100 text-indigo-800 border-indigo-200",
  seminar: "bg-amber-100 text-amber-800 border-amber-200",
  workshop: "bg-rose-100 text-rose-800 border-rose-200",
};

async function apiFetch(path: string, opts?: RequestInit) {
  const res = await fetch(`${basePath}${path}`, { credentials: "include", ...opts });
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error((b as { error?: string }).error ?? "Request failed");
  }
  if (res.status === 204) return null;
  return res.json();
}

function EventForm({ value, onChange }: { value: EForm; onChange: (v: EForm) => void }) {
  const set = (k: string, v: unknown) => onChange({ ...value, [k]: v });
  return (
    <div className="space-y-3">
      <div>
        <Label>Title *</Label>
        <Input value={value.title} onChange={e => set("title", e.target.value)} placeholder="Event title" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Type *</Label>
          <Select value={value.type} onValueChange={v => set("type", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(TYPE_LABELS).map(([v, l]) => (
                <SelectItem key={v} value={v}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Organiser *</Label>
          <Input value={value.organiser} onChange={e => set("organiser", e.target.value)} placeholder="e.g. BSR" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Start date *</Label>
          <Input type="datetime-local" value={value.date} onChange={e => set("date", e.target.value)} />
        </div>
        <div>
          <Label>End date</Label>
          <Input type="datetime-local" value={value.endDate ?? ""} onChange={e => set("endDate", e.target.value || null)} />
        </div>
      </div>
      <div>
        <Label>URL *</Label>
        <Input value={value.url} onChange={e => set("url", e.target.value)} placeholder="https://..." />
      </div>
      <div>
        <Label>Location</Label>
        <Input value={value.location ?? ""} onChange={e => set("location", e.target.value || null)} placeholder="e.g. Manchester Central, or Online" />
      </div>
      <div>
        <Label>Description</Label>
        <Textarea value={value.description ?? ""} onChange={e => set("description", e.target.value || null)} rows={3} placeholder="Brief event description…" />
      </div>
      <div className="flex items-center gap-3">
        <Switch checked={value.isFeatured} onCheckedChange={v => set("isFeatured", v)} id="featured" />
        <Label htmlFor="featured">Featured event</Label>
      </div>
    </div>
  );
}

export default function AdminEvents() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Event | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Event | null>(null);
  const [form, setForm] = useState<EForm>(EMPTY);

  const { data: events = [], isLoading } = useQuery<Event[]>({
    queryKey: ["admin-events"],
    queryFn: () => apiFetch("/api/admin/events"),
  });

  const createMutation = useMutation({
    mutationFn: (data: EForm) =>
      apiFetch("/api/admin/events", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-events"] });
      toast({ title: "Event created" });
      setFormOpen(false);
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<EForm> }) =>
      apiFetch(`/api/admin/events/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-events"] });
      toast({ title: "Event updated" });
      setFormOpen(false);
      setEditTarget(null);
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      apiFetch(`/api/admin/events/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-events"] });
      toast({ title: "Event deleted" });
      setDeleteTarget(null);
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const openCreate = () => {
    setEditTarget(null);
    setForm(EMPTY);
    setFormOpen(true);
  };

  const openEdit = (ev: Event) => {
    setEditTarget(ev);
    setForm({
      title: ev.title,
      type: ev.type,
      organiser: ev.organiser,
      date: ev.date.slice(0, 16),
      endDate: ev.endDate ? ev.endDate.slice(0, 16) : "",
      location: ev.location ?? "",
      url: ev.url,
      description: ev.description ?? "",
      isFeatured: ev.isFeatured,
    });
    setFormOpen(true);
  };

  const handleSubmit = () => {
    if (!form.title || !form.url || !form.organiser || !form.date) {
      toast({ title: "Required fields missing", variant: "destructive" });
      return;
    }
    const payload = {
      ...form,
      endDate: form.endDate || null,
      location: form.location || null,
      description: form.description || null,
    };
    if (editTarget) {
      updateMutation.mutate({ id: editTarget.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Events"
        description="Manage rheumatology conferences, webinars, seminars, and workshops."
        action={
          <Button onClick={openCreate} size="sm">
            <Plus className="h-4 w-4 mr-1" /> Add Event
          </Button>
        }
      />

      <AdminTable
        columns={COLUMNS}
        emptyMessage="No events. Add the first one."
        rows={
          isLoading
            ? []
            : events.map(ev => [
                <div className="flex items-start gap-2">
                  <CalendarDays className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-slate-900">{ev.title}</p>
                    <p className="text-xs text-slate-500">{ev.organiser}</p>
                  </div>
                </div>,
                <Badge variant="outline" className={`text-xs ${TYPE_COLOURS[ev.type] ?? ""}`}>
                  {TYPE_LABELS[ev.type] ?? ev.type}
                </Badge>,
                <span>
                  {format(parseISO(ev.date), "d MMM yyyy")}
                  {ev.endDate && (
                    <span className="text-slate-400"> – {format(parseISO(ev.endDate), "d MMM yyyy")}</span>
                  )}
                </span>,
                <span className="text-slate-600">{ev.location ?? "—"}</span>,
                ev.isFeatured ? (
                  <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-xs">Featured</Badge>
                ) : (
                  <span className="text-slate-400 text-xs">—</span>
                ),
                <div className="flex items-center gap-1">
                  <a
                    href={ev.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 text-slate-400 hover:text-teal-600 transition-colors"
                    title="Open event URL"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(ev)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-red-400 hover:text-red-600"
                    onClick={() => setDeleteTarget(ev)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>,
              ])
        }
      />

      <Dialog open={formOpen} onOpenChange={open => { setFormOpen(open); if (!open) setEditTarget(null); }}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editTarget ? "Edit Event" : "Add Event"}</DialogTitle>
          </DialogHeader>
          <EventForm value={form} onChange={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={isPending}>
              {isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editTarget ? "Save Changes" : "Create Event"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete event?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{deleteTarget?.title}</strong>. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            >
              {deleteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
